/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  BackfillCustomViewExecutionsPathParameters,
  CreateCustomViewForDashboardPathParameters,
  CreateCustomViewGroupPathParameters,
  CreateCustomViewGroupSourcePathParameters,
  DeleteCustomViewByIdPathParameters,
  DeleteCustomViewGroupByIdPathParameters,
  DeleteCustomViewGroupSourceByIdPathParameters,
  ExportCustomViewExecutionDataByIdPathParameters,
  GenerateFiltersForCustomViewExecutionPathParameters,
  GetAllCustomViewExecutionsByViewPathParameters,
  GetAllCustomViewsByDashboardParams,
  GetAllCustomViewsByDashboardPathParameters,
  GetCustomViewByIdPathParameters,
  GetCustomViewExecutionByIdPathParameters,
  GetCustomViewExecutionDataByIdPathParameters,
  GetCustomViewExecutionFilterValuesPathParameters,
  GetCustomViewLinksByIdPathParameters,
  RecalculateCustomViewExecutionByIdPathParameters,
  ReplaceCustomViewPathParameters,
  TLCustomViewBackfillRequest,
  TLCustomViewBackfillResponse,
  TLCustomViewCreateRequest,
  TLCustomViewExecutionResponse,
  TLCustomViewExecutionsResponse,
  TLCustomViewFilterValuesResponse,
  TLCustomViewGroupCreateRequest,
  TLCustomViewGroupUpdateRequest,
  TLCustomViewLinksResponse,
  TLCustomViewPreviewResponse,
  TLCustomViewReplaceRequest,
  TLCustomViewResponse,
  TLCustomViewSourceCreateRequest,
  TLCustomViewSourcePreviewResponse,
  TLCustomViewSourceUpdateRequest,
  TLCustomViewsResponse,
  TLCustomViewUpdateRequest,
  TLDataHarmonizationV2GroupResponse,
  TLDeleteCustomViewExecutionsRequest,
  TLExportExecutionRequest,
  TLExportExecutionResponse,
  TLGenerateFiltersForViewExecutionRequest,
  TLGenerateFiltersResponse,
  TLGetExecutionDataRequest,
  TLGetExecutionDataResponse,
  TLHTTPValidationError,
  TLPreviewGroupRequest,
  TLPreviewSourceRequest,
  TLPreviewViewRequest,
  TLSourceResponse,
  TLSuccessResponse,
  TriggerCustomViewExecutionPathParameters,
  UpdateCustomViewByIdPathParameters,
  UpdateCustomViewGroupByIdPathParameters,
  UpdateCustomViewGroupSourceByIdPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Custom Views By Dashboard
 */
export const getAllCustomViewsByDashboard = (
  { dashboardId }: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewsResponse>(
    { url: `/v2/data-harmonization/custom-views/dashboard/${dashboardId}`, method: 'GET', params, signal },
    options,
  );
};

export const getGetAllCustomViewsByDashboardQueryKey = (
  { dashboardId }: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
) => {
  return ['v2', 'data-harmonization', 'custom-views', 'dashboard', dashboardId, ...(params ? [params] : [])] as const;
};

export const getGetAllCustomViewsByDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllCustomViewsByDashboardQueryKey({ dashboardId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>> = ({ signal }) =>
    getAllCustomViewsByDashboard({ dashboardId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAllCustomViewsByDashboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>
>;
export type GetAllCustomViewsByDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllCustomViewsByDashboard<
  TData = Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllCustomViewsByDashboardPathParameters,
  params: undefined | GetAllCustomViewsByDashboardParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
          TError,
          Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCustomViewsByDashboard<
  TData = Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
          TError,
          Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCustomViewsByDashboard<
  TData = Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Custom Views By Dashboard
 */

export function useGetAllCustomViewsByDashboard<
  TData = Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllCustomViewsByDashboardQueryOptions({ dashboardId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Custom Views By Dashboard
 */
export const prefetchGetAllCustomViewsByDashboardQuery = async <
  TData = Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllCustomViewsByDashboardQueryOptions({ dashboardId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Custom Views By Dashboard
 */
export const invalidateGetAllCustomViewsByDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: GetAllCustomViewsByDashboardPathParameters,
  params?: GetAllCustomViewsByDashboardParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetAllCustomViewsByDashboardQueryKey({ dashboardId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Custom View For Dashboard
 */
export const createCustomViewForDashboard = (
  { dashboardId }: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewResponse>(
    {
      url: `/v2/data-harmonization/custom-views/dashboard/${dashboardId}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateCustomViewForDashboardQueryKey = (
  { dashboardId }: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest?: BodyType<TLCustomViewCreateRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'custom-views',
    'dashboard',
    dashboardId,
    tLCustomViewCreateRequest,
  ] as const;
};

export const getCreateCustomViewForDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof createCustomViewForDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateCustomViewForDashboardQueryKey({ dashboardId }, tLCustomViewCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCustomViewForDashboard>>> = ({ signal }) =>
    createCustomViewForDashboard({ dashboardId }, tLCustomViewCreateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateCustomViewForDashboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof createCustomViewForDashboard>>
>;
export type CreateCustomViewForDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCustomViewForDashboard<
  TData = Awaited<ReturnType<typeof createCustomViewForDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCustomViewForDashboard>>,
          TError,
          Awaited<ReturnType<typeof createCustomViewForDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCustomViewForDashboard<
  TData = Awaited<ReturnType<typeof createCustomViewForDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCustomViewForDashboard>>,
          TError,
          Awaited<ReturnType<typeof createCustomViewForDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCustomViewForDashboard<
  TData = Awaited<ReturnType<typeof createCustomViewForDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Custom View For Dashboard
 */

export function useCreateCustomViewForDashboard<
  TData = Awaited<ReturnType<typeof createCustomViewForDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCustomViewForDashboardQueryOptions({ dashboardId }, tLCustomViewCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Custom View For Dashboard
 */
export const prefetchCreateCustomViewForDashboardQuery = async <
  TData = Awaited<ReturnType<typeof createCustomViewForDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewForDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCustomViewForDashboardQueryOptions({ dashboardId }, tLCustomViewCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Custom View For Dashboard
 */
export const invalidateCreateCustomViewForDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: CreateCustomViewForDashboardPathParameters,
  tLCustomViewCreateRequest: BodyType<TLCustomViewCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateCustomViewForDashboardQueryKey({ dashboardId }, tLCustomViewCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Custom View Executions
 */
export const deleteCustomViewExecutions = (
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    {
      url: `/v2/data-harmonization/custom-views/executions`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLDeleteCustomViewExecutionsRequest,
      signal,
    },
    options,
  );
};

export const getDeleteCustomViewExecutionsQueryKey = (
  tLDeleteCustomViewExecutionsRequest?: BodyType<TLDeleteCustomViewExecutionsRequest>,
) => {
  return [
    'DELETE',
    'v2',
    'data-harmonization',
    'custom-views',
    'executions',
    tLDeleteCustomViewExecutionsRequest,
  ] as const;
};

export const getDeleteCustomViewExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCustomViewExecutionsQueryKey(tLDeleteCustomViewExecutionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCustomViewExecutions>>> = ({ signal }) =>
    deleteCustomViewExecutions(tLDeleteCustomViewExecutionsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteCustomViewExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCustomViewExecutions>>>;
export type DeleteCustomViewExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCustomViewExecutions<
  TData = Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewExecutions<
  TData = Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewExecutions<
  TData = Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Custom View Executions
 */

export function useDeleteCustomViewExecutions<
  TData = Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCustomViewExecutionsQueryOptions(tLDeleteCustomViewExecutionsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Custom View Executions
 */
export const prefetchDeleteCustomViewExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof deleteCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCustomViewExecutionsQueryOptions(tLDeleteCustomViewExecutionsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Custom View Executions
 */
export const invalidateDeleteCustomViewExecutions = async (
  queryClient: QueryClient,
  tLDeleteCustomViewExecutionsRequest: BodyType<TLDeleteCustomViewExecutionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteCustomViewExecutionsQueryKey(tLDeleteCustomViewExecutionsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Custom View Execution By Id
 */
export const getCustomViewExecutionById = (
  { executionId }: GetCustomViewExecutionByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewExecutionResponse>(
    { url: `/v2/data-harmonization/custom-views/executions/${executionId}`, method: 'GET', signal },
    options,
  );
};

export const getGetCustomViewExecutionByIdQueryKey = ({ executionId }: GetCustomViewExecutionByIdPathParameters) => {
  return ['v2', 'data-harmonization', 'custom-views', 'executions', executionId] as const;
};

export const getGetCustomViewExecutionByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCustomViewExecutionByIdQueryKey({ executionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomViewExecutionById>>> = ({ signal }) =>
    getCustomViewExecutionById({ executionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCustomViewExecutionByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getCustomViewExecutionById>>>;
export type GetCustomViewExecutionByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewExecutionById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewExecutionById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewExecutionById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewExecutionById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Custom View Execution By Id
 */

export function useGetCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCustomViewExecutionByIdQueryOptions({ executionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Custom View Execution By Id
 */
export const prefetchGetCustomViewExecutionByIdQuery = async <
  TData = Awaited<ReturnType<typeof getCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: GetCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCustomViewExecutionByIdQueryOptions({ executionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Custom View Execution By Id
 */
export const invalidateGetCustomViewExecutionById = async (
  queryClient: QueryClient,
  { executionId }: GetCustomViewExecutionByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCustomViewExecutionByIdQueryKey({ executionId }) }, options);

  return queryClient;
};

/**
 * @summary Get Custom View Execution Data By Id
 */
export const getCustomViewExecutionDataById = (
  { executionId }: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGetExecutionDataResponse>(
    {
      url: `/v2/data-harmonization/custom-views/executions/${executionId}/data`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGetExecutionDataRequest,
      signal,
    },
    options,
  );
};

export const getGetCustomViewExecutionDataByIdQueryKey = (
  { executionId }: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest?: BodyType<TLGetExecutionDataRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'custom-views',
    'executions',
    executionId,
    'data',
    tLGetExecutionDataRequest,
  ] as const;
};

export const getGetCustomViewExecutionDataByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetCustomViewExecutionDataByIdQueryKey({ executionId }, tLGetExecutionDataRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>> = ({ signal }) =>
    getCustomViewExecutionDataById({ executionId }, tLGetExecutionDataRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCustomViewExecutionDataByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCustomViewExecutionDataById>>
>;
export type GetCustomViewExecutionDataByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewExecutionDataById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewExecutionDataById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Custom View Execution Data By Id
 */

export function useGetCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCustomViewExecutionDataByIdQueryOptions(
    { executionId },
    tLGetExecutionDataRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Custom View Execution Data By Id
 */
export const prefetchGetCustomViewExecutionDataByIdQuery = async <
  TData = Awaited<ReturnType<typeof getCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCustomViewExecutionDataByIdQueryOptions(
    { executionId },
    tLGetExecutionDataRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Custom View Execution Data By Id
 */
export const invalidateGetCustomViewExecutionDataById = async (
  queryClient: QueryClient,
  { executionId }: GetCustomViewExecutionDataByIdPathParameters,
  tLGetExecutionDataRequest: BodyType<TLGetExecutionDataRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetCustomViewExecutionDataByIdQueryKey({ executionId }, tLGetExecutionDataRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Export Custom View Execution Data By Id
 */
export const exportCustomViewExecutionDataById = (
  { executionId }: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLExportExecutionResponse>(
    {
      url: `/v2/data-harmonization/custom-views/executions/${executionId}/export`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExportExecutionRequest,
      signal,
    },
    options,
  );
};

export const getExportCustomViewExecutionDataByIdQueryKey = (
  { executionId }: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest?: BodyType<TLExportExecutionRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'custom-views',
    'executions',
    executionId,
    'export',
    tLExportExecutionRequest,
  ] as const;
};

export const getExportCustomViewExecutionDataByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getExportCustomViewExecutionDataByIdQueryKey({ executionId }, tLExportExecutionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>> = ({ signal }) =>
    exportCustomViewExecutionDataById({ executionId }, tLExportExecutionRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportCustomViewExecutionDataByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>
>;
export type ExportCustomViewExecutionDataByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useExportCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
          TError,
          Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
          TError,
          Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Custom View Execution Data By Id
 */

export function useExportCustomViewExecutionDataById<
  TData = Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportCustomViewExecutionDataByIdQueryOptions(
    { executionId },
    tLExportExecutionRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Custom View Execution Data By Id
 */
export const prefetchExportCustomViewExecutionDataByIdQuery = async <
  TData = Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportCustomViewExecutionDataById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportCustomViewExecutionDataByIdQueryOptions(
    { executionId },
    tLExportExecutionRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Custom View Execution Data By Id
 */
export const invalidateExportCustomViewExecutionDataById = async (
  queryClient: QueryClient,
  { executionId }: ExportCustomViewExecutionDataByIdPathParameters,
  tLExportExecutionRequest: BodyType<TLExportExecutionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportCustomViewExecutionDataByIdQueryKey({ executionId }, tLExportExecutionRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Custom View Execution Filter Values
 */
export const getCustomViewExecutionFilterValues = (
  { executionId }: GetCustomViewExecutionFilterValuesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewFilterValuesResponse>(
    { url: `/v2/data-harmonization/custom-views/executions/${executionId}/filter-values`, method: 'GET', signal },
    options,
  );
};

export const getGetCustomViewExecutionFilterValuesQueryKey = ({
  executionId,
}: GetCustomViewExecutionFilterValuesPathParameters) => {
  return ['v2', 'data-harmonization', 'custom-views', 'executions', executionId, 'filter-values'] as const;
};

export const getGetCustomViewExecutionFilterValuesQueryOptions = <
  TData = Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetCustomViewExecutionFilterValuesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCustomViewExecutionFilterValuesQueryKey({ executionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>> = ({ signal }) =>
    getCustomViewExecutionFilterValues({ executionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetCustomViewExecutionFilterValuesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>
>;
export type GetCustomViewExecutionFilterValuesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCustomViewExecutionFilterValues<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionFilterValuesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewExecutionFilterValues<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionFilterValuesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewExecutionFilterValues<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewExecutionFilterValuesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Custom View Execution Filter Values
 */

export function useGetCustomViewExecutionFilterValues<
  TData = Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetCustomViewExecutionFilterValuesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCustomViewExecutionFilterValuesQueryOptions({ executionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Custom View Execution Filter Values
 */
export const prefetchGetCustomViewExecutionFilterValuesQuery = async <
  TData = Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: GetCustomViewExecutionFilterValuesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewExecutionFilterValues>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCustomViewExecutionFilterValuesQueryOptions({ executionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Custom View Execution Filter Values
 */
export const invalidateGetCustomViewExecutionFilterValues = async (
  queryClient: QueryClient,
  { executionId }: GetCustomViewExecutionFilterValuesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetCustomViewExecutionFilterValuesQueryKey({ executionId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Generate Filters For Custom View Execution
 */
export const generateFiltersForCustomViewExecution = (
  { executionId }: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGenerateFiltersResponse>(
    {
      url: `/v2/data-harmonization/custom-views/executions/${executionId}/generate-filters`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGenerateFiltersForViewExecutionRequest,
      signal,
    },
    options,
  );
};

export const getGenerateFiltersForCustomViewExecutionQueryKey = (
  { executionId }: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest?: BodyType<TLGenerateFiltersForViewExecutionRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'custom-views',
    'executions',
    executionId,
    'generate-filters',
    tLGenerateFiltersForViewExecutionRequest,
  ] as const;
};

export const getGenerateFiltersForCustomViewExecutionQueryOptions = <
  TData = Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGenerateFiltersForCustomViewExecutionQueryKey({ executionId }, tLGenerateFiltersForViewExecutionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>> = ({ signal }) =>
    generateFiltersForCustomViewExecution(
      { executionId },
      tLGenerateFiltersForViewExecutionRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GenerateFiltersForCustomViewExecutionQueryResult = NonNullable<
  Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>
>;
export type GenerateFiltersForCustomViewExecutionQueryError = ErrorType<TLHTTPValidationError>;

export function useGenerateFiltersForCustomViewExecution<
  TData = Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
          TError,
          Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFiltersForCustomViewExecution<
  TData = Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
          TError,
          Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFiltersForCustomViewExecution<
  TData = Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Generate Filters For Custom View Execution
 */

export function useGenerateFiltersForCustomViewExecution<
  TData = Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGenerateFiltersForCustomViewExecutionQueryOptions(
    { executionId },
    tLGenerateFiltersForViewExecutionRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Generate Filters For Custom View Execution
 */
export const prefetchGenerateFiltersForCustomViewExecutionQuery = async <
  TData = Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGenerateFiltersForCustomViewExecutionQueryOptions(
    { executionId },
    tLGenerateFiltersForViewExecutionRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Generate Filters For Custom View Execution
 */
export const invalidateGenerateFiltersForCustomViewExecution = async (
  queryClient: QueryClient,
  { executionId }: GenerateFiltersForCustomViewExecutionPathParameters,
  tLGenerateFiltersForViewExecutionRequest: BodyType<TLGenerateFiltersForViewExecutionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getGenerateFiltersForCustomViewExecutionQueryKey(
        { executionId },
        tLGenerateFiltersForViewExecutionRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Recalculate Custom View Execution By Id
 */
export const recalculateCustomViewExecutionById = (
  { executionId }: RecalculateCustomViewExecutionByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewExecutionResponse>(
    { url: `/v2/data-harmonization/custom-views/executions/${executionId}/recalculate`, method: 'POST', signal },
    options,
  );
};

export const getRecalculateCustomViewExecutionByIdQueryKey = ({
  executionId,
}: RecalculateCustomViewExecutionByIdPathParameters) => {
  return ['POST', 'v2', 'data-harmonization', 'custom-views', 'executions', executionId, 'recalculate'] as const;
};

export const getRecalculateCustomViewExecutionByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: RecalculateCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRecalculateCustomViewExecutionByIdQueryKey({ executionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>> = ({ signal }) =>
    recalculateCustomViewExecutionById({ executionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RecalculateCustomViewExecutionByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>
>;
export type RecalculateCustomViewExecutionByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useRecalculateCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RecalculateCustomViewExecutionByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
          TError,
          Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRecalculateCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RecalculateCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
          TError,
          Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRecalculateCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RecalculateCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Recalculate Custom View Execution By Id
 */

export function useRecalculateCustomViewExecutionById<
  TData = Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: RecalculateCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRecalculateCustomViewExecutionByIdQueryOptions({ executionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Recalculate Custom View Execution By Id
 */
export const prefetchRecalculateCustomViewExecutionByIdQuery = async <
  TData = Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: RecalculateCustomViewExecutionByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof recalculateCustomViewExecutionById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRecalculateCustomViewExecutionByIdQueryOptions({ executionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Recalculate Custom View Execution By Id
 */
export const invalidateRecalculateCustomViewExecutionById = async (
  queryClient: QueryClient,
  { executionId }: RecalculateCustomViewExecutionByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRecalculateCustomViewExecutionByIdQueryKey({ executionId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Custom View Group By Id
 */
export const deleteCustomViewGroupById = (
  { groupId }: DeleteCustomViewGroupByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/custom-views/groups/${groupId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCustomViewGroupByIdQueryKey = ({ groupId }: DeleteCustomViewGroupByIdPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'custom-views', 'groups', groupId] as const;
};

export const getDeleteCustomViewGroupByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: DeleteCustomViewGroupByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCustomViewGroupByIdQueryKey({ groupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCustomViewGroupById>>> = ({ signal }) =>
    deleteCustomViewGroupById({ groupId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteCustomViewGroupByIdQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCustomViewGroupById>>>;
export type DeleteCustomViewGroupByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCustomViewGroupById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewGroupByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewGroupById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewGroupById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewGroupByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewGroupById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewGroupById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewGroupByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Custom View Group By Id
 */

export function useDeleteCustomViewGroupById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: DeleteCustomViewGroupByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCustomViewGroupByIdQueryOptions({ groupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Custom View Group By Id
 */
export const prefetchDeleteCustomViewGroupByIdQuery = async <
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: DeleteCustomViewGroupByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCustomViewGroupByIdQueryOptions({ groupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Custom View Group By Id
 */
export const invalidateDeleteCustomViewGroupById = async (
  queryClient: QueryClient,
  { groupId }: DeleteCustomViewGroupByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteCustomViewGroupByIdQueryKey({ groupId }) }, options);

  return queryClient;
};

/**
 * @summary Update Custom View Group By Id
 */
export const updateCustomViewGroupById = (
  { groupId }: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataHarmonizationV2GroupResponse>(
    {
      url: `/v2/data-harmonization/custom-views/groups/${groupId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewGroupUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateCustomViewGroupByIdQueryKey = (
  { groupId }: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest?: BodyType<TLCustomViewGroupUpdateRequest>,
) => {
  return [
    'PATCH',
    'v2',
    'data-harmonization',
    'custom-views',
    'groups',
    groupId,
    tLCustomViewGroupUpdateRequest,
  ] as const;
};

export const getUpdateCustomViewGroupByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof updateCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateCustomViewGroupByIdQueryKey({ groupId }, tLCustomViewGroupUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateCustomViewGroupById>>> = ({ signal }) =>
    updateCustomViewGroupById({ groupId }, tLCustomViewGroupUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateCustomViewGroupById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateCustomViewGroupByIdQueryResult = NonNullable<Awaited<ReturnType<typeof updateCustomViewGroupById>>>;
export type UpdateCustomViewGroupByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateCustomViewGroupById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCustomViewGroupById>>,
          TError,
          Awaited<ReturnType<typeof updateCustomViewGroupById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCustomViewGroupById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCustomViewGroupById>>,
          TError,
          Awaited<ReturnType<typeof updateCustomViewGroupById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCustomViewGroupById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Custom View Group By Id
 */

export function useUpdateCustomViewGroupById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateCustomViewGroupByIdQueryOptions({ groupId }, tLCustomViewGroupUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Custom View Group By Id
 */
export const prefetchUpdateCustomViewGroupByIdQuery = async <
  TData = Awaited<ReturnType<typeof updateCustomViewGroupById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateCustomViewGroupByIdQueryOptions({ groupId }, tLCustomViewGroupUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Custom View Group By Id
 */
export const invalidateUpdateCustomViewGroupById = async (
  queryClient: QueryClient,
  { groupId }: UpdateCustomViewGroupByIdPathParameters,
  tLCustomViewGroupUpdateRequest: BodyType<TLCustomViewGroupUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateCustomViewGroupByIdQueryKey({ groupId }, tLCustomViewGroupUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Custom View Group Source
 */
export const createCustomViewGroupSource = (
  { groupId }: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSourceResponse>(
    {
      url: `/v2/data-harmonization/custom-views/groups/${groupId}/sources`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewSourceCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateCustomViewGroupSourceQueryKey = (
  { groupId }: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest?: BodyType<TLCustomViewSourceCreateRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'custom-views',
    'groups',
    groupId,
    'sources',
    tLCustomViewSourceCreateRequest,
  ] as const;
};

export const getCreateCustomViewGroupSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof createCustomViewGroupSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroupSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateCustomViewGroupSourceQueryKey({ groupId }, tLCustomViewSourceCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCustomViewGroupSource>>> = ({ signal }) =>
    createCustomViewGroupSource({ groupId }, tLCustomViewSourceCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createCustomViewGroupSource>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateCustomViewGroupSourceQueryResult = NonNullable<
  Awaited<ReturnType<typeof createCustomViewGroupSource>>
>;
export type CreateCustomViewGroupSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCustomViewGroupSource<
  TData = Awaited<ReturnType<typeof createCustomViewGroupSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroupSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCustomViewGroupSource>>,
          TError,
          Awaited<ReturnType<typeof createCustomViewGroupSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCustomViewGroupSource<
  TData = Awaited<ReturnType<typeof createCustomViewGroupSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroupSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCustomViewGroupSource>>,
          TError,
          Awaited<ReturnType<typeof createCustomViewGroupSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCustomViewGroupSource<
  TData = Awaited<ReturnType<typeof createCustomViewGroupSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroupSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Custom View Group Source
 */

export function useCreateCustomViewGroupSource<
  TData = Awaited<ReturnType<typeof createCustomViewGroupSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroupSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCustomViewGroupSourceQueryOptions(
    { groupId },
    tLCustomViewSourceCreateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Custom View Group Source
 */
export const prefetchCreateCustomViewGroupSourceQuery = async <
  TData = Awaited<ReturnType<typeof createCustomViewGroupSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroupSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCustomViewGroupSourceQueryOptions(
    { groupId },
    tLCustomViewSourceCreateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Custom View Group Source
 */
export const invalidateCreateCustomViewGroupSource = async (
  queryClient: QueryClient,
  { groupId }: CreateCustomViewGroupSourcePathParameters,
  tLCustomViewSourceCreateRequest: BodyType<TLCustomViewSourceCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateCustomViewGroupSourceQueryKey({ groupId }, tLCustomViewSourceCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Preview Custom View Group
 */
export const previewCustomViewGroup = (
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewPreviewResponse>(
    {
      url: `/v2/data-harmonization/custom-views/preview/group`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLPreviewGroupRequest,
      signal,
    },
    options,
  );
};

export const getPreviewCustomViewGroupQueryKey = (tLPreviewGroupRequest?: BodyType<TLPreviewGroupRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'custom-views', 'preview', 'group', tLPreviewGroupRequest] as const;
};

export const getPreviewCustomViewGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof previewCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewCustomViewGroupQueryKey(tLPreviewGroupRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewCustomViewGroup>>> = ({ signal }) =>
    previewCustomViewGroup(tLPreviewGroupRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof previewCustomViewGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PreviewCustomViewGroupQueryResult = NonNullable<Awaited<ReturnType<typeof previewCustomViewGroup>>>;
export type PreviewCustomViewGroupQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewCustomViewGroup<
  TData = Awaited<ReturnType<typeof previewCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewCustomViewGroup>>,
          TError,
          Awaited<ReturnType<typeof previewCustomViewGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewCustomViewGroup<
  TData = Awaited<ReturnType<typeof previewCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewCustomViewGroup>>,
          TError,
          Awaited<ReturnType<typeof previewCustomViewGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewCustomViewGroup<
  TData = Awaited<ReturnType<typeof previewCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Custom View Group
 */

export function usePreviewCustomViewGroup<
  TData = Awaited<ReturnType<typeof previewCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewCustomViewGroupQueryOptions(tLPreviewGroupRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Custom View Group
 */
export const prefetchPreviewCustomViewGroupQuery = async <
  TData = Awaited<ReturnType<typeof previewCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewCustomViewGroupQueryOptions(tLPreviewGroupRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Custom View Group
 */
export const invalidatePreviewCustomViewGroup = async (
  queryClient: QueryClient,
  tLPreviewGroupRequest: BodyType<TLPreviewGroupRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPreviewCustomViewGroupQueryKey(tLPreviewGroupRequest) }, options);

  return queryClient;
};

/**
 * @summary Preview Custom View Source
 */
export const previewCustomViewSource = (
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewSourcePreviewResponse>(
    {
      url: `/v2/data-harmonization/custom-views/preview/source`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLPreviewSourceRequest,
      signal,
    },
    options,
  );
};

export const getPreviewCustomViewSourceQueryKey = (tLPreviewSourceRequest?: BodyType<TLPreviewSourceRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'custom-views', 'preview', 'source', tLPreviewSourceRequest] as const;
};

export const getPreviewCustomViewSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof previewCustomViewSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewCustomViewSourceQueryKey(tLPreviewSourceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewCustomViewSource>>> = ({ signal }) =>
    previewCustomViewSource(tLPreviewSourceRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof previewCustomViewSource>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PreviewCustomViewSourceQueryResult = NonNullable<Awaited<ReturnType<typeof previewCustomViewSource>>>;
export type PreviewCustomViewSourceQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewCustomViewSource<
  TData = Awaited<ReturnType<typeof previewCustomViewSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewCustomViewSource>>,
          TError,
          Awaited<ReturnType<typeof previewCustomViewSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewCustomViewSource<
  TData = Awaited<ReturnType<typeof previewCustomViewSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewCustomViewSource>>,
          TError,
          Awaited<ReturnType<typeof previewCustomViewSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewCustomViewSource<
  TData = Awaited<ReturnType<typeof previewCustomViewSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Custom View Source
 */

export function usePreviewCustomViewSource<
  TData = Awaited<ReturnType<typeof previewCustomViewSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewCustomViewSourceQueryOptions(tLPreviewSourceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Custom View Source
 */
export const prefetchPreviewCustomViewSourceQuery = async <
  TData = Awaited<ReturnType<typeof previewCustomViewSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomViewSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewCustomViewSourceQueryOptions(tLPreviewSourceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Custom View Source
 */
export const invalidatePreviewCustomViewSource = async (
  queryClient: QueryClient,
  tLPreviewSourceRequest: BodyType<TLPreviewSourceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPreviewCustomViewSourceQueryKey(tLPreviewSourceRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Preview Custom View
 */
export const previewCustomView = (
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewPreviewResponse>(
    {
      url: `/v2/data-harmonization/custom-views/preview/view`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLPreviewViewRequest,
      signal,
    },
    options,
  );
};

export const getPreviewCustomViewQueryKey = (tLPreviewViewRequest?: BodyType<TLPreviewViewRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'custom-views', 'preview', 'view', tLPreviewViewRequest] as const;
};

export const getPreviewCustomViewQueryOptions = <
  TData = Awaited<ReturnType<typeof previewCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewCustomViewQueryKey(tLPreviewViewRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewCustomView>>> = ({ signal }) =>
    previewCustomView(tLPreviewViewRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof previewCustomView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PreviewCustomViewQueryResult = NonNullable<Awaited<ReturnType<typeof previewCustomView>>>;
export type PreviewCustomViewQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewCustomView<
  TData = Awaited<ReturnType<typeof previewCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewCustomView>>,
          TError,
          Awaited<ReturnType<typeof previewCustomView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewCustomView<
  TData = Awaited<ReturnType<typeof previewCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewCustomView>>,
          TError,
          Awaited<ReturnType<typeof previewCustomView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewCustomView<
  TData = Awaited<ReturnType<typeof previewCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Custom View
 */

export function usePreviewCustomView<
  TData = Awaited<ReturnType<typeof previewCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewCustomViewQueryOptions(tLPreviewViewRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Custom View
 */
export const prefetchPreviewCustomViewQuery = async <
  TData = Awaited<ReturnType<typeof previewCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewCustomViewQueryOptions(tLPreviewViewRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Custom View
 */
export const invalidatePreviewCustomView = async (
  queryClient: QueryClient,
  tLPreviewViewRequest: BodyType<TLPreviewViewRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPreviewCustomViewQueryKey(tLPreviewViewRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Custom View Group Source By Id
 */
export const deleteCustomViewGroupSourceById = (
  { sourceId }: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/custom-views/sources/${sourceId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCustomViewGroupSourceByIdQueryKey = ({
  sourceId,
}: DeleteCustomViewGroupSourceByIdPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'custom-views', 'sources', sourceId] as const;
};

export const getDeleteCustomViewGroupSourceByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCustomViewGroupSourceByIdQueryKey({ sourceId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>> = ({ signal }) =>
    deleteCustomViewGroupSourceById({ sourceId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteCustomViewGroupSourceByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>
>;
export type DeleteCustomViewGroupSourceByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewGroupSourceByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Custom View Group Source By Id
 */

export function useDeleteCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCustomViewGroupSourceByIdQueryOptions({ sourceId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Custom View Group Source By Id
 */
export const prefetchDeleteCustomViewGroupSourceByIdQuery = async <
  TData = Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCustomViewGroupSourceByIdQueryOptions({ sourceId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Custom View Group Source By Id
 */
export const invalidateDeleteCustomViewGroupSourceById = async (
  queryClient: QueryClient,
  { sourceId }: DeleteCustomViewGroupSourceByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteCustomViewGroupSourceByIdQueryKey({ sourceId }) }, options);

  return queryClient;
};

/**
 * @summary Update Custom View Group Source By Id
 */
export const updateCustomViewGroupSourceById = (
  { sourceId }: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSourceResponse>(
    {
      url: `/v2/data-harmonization/custom-views/sources/${sourceId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewSourceUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateCustomViewGroupSourceByIdQueryKey = (
  { sourceId }: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest?: BodyType<TLCustomViewSourceUpdateRequest>,
) => {
  return [
    'PATCH',
    'v2',
    'data-harmonization',
    'custom-views',
    'sources',
    sourceId,
    tLCustomViewSourceUpdateRequest,
  ] as const;
};

export const getUpdateCustomViewGroupSourceByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateCustomViewGroupSourceByIdQueryKey({ sourceId }, tLCustomViewSourceUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>> = ({ signal }) =>
    updateCustomViewGroupSourceById({ sourceId }, tLCustomViewSourceUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateCustomViewGroupSourceByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>
>;
export type UpdateCustomViewGroupSourceByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
          TError,
          Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
          TError,
          Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Custom View Group Source By Id
 */

export function useUpdateCustomViewGroupSourceById<
  TData = Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateCustomViewGroupSourceByIdQueryOptions(
    { sourceId },
    tLCustomViewSourceUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Custom View Group Source By Id
 */
export const prefetchUpdateCustomViewGroupSourceByIdQuery = async <
  TData = Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewGroupSourceById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateCustomViewGroupSourceByIdQueryOptions(
    { sourceId },
    tLCustomViewSourceUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Custom View Group Source By Id
 */
export const invalidateUpdateCustomViewGroupSourceById = async (
  queryClient: QueryClient,
  { sourceId }: UpdateCustomViewGroupSourceByIdPathParameters,
  tLCustomViewSourceUpdateRequest: BodyType<TLCustomViewSourceUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateCustomViewGroupSourceByIdQueryKey({ sourceId }, tLCustomViewSourceUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Custom View By Id
 */
export const deleteCustomViewById = (
  { viewId }: DeleteCustomViewByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/custom-views/${viewId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCustomViewByIdQueryKey = ({ viewId }: DeleteCustomViewByIdPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'custom-views', viewId] as const;
};

export const getDeleteCustomViewByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: DeleteCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCustomViewByIdQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCustomViewById>>> = ({ signal }) =>
    deleteCustomViewById({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteCustomViewById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteCustomViewByIdQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCustomViewById>>>;
export type DeleteCustomViewByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCustomViewById<
  TData = Awaited<ReturnType<typeof deleteCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewById>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewById<
  TData = Awaited<ReturnType<typeof deleteCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCustomViewById>>,
          TError,
          Awaited<ReturnType<typeof deleteCustomViewById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCustomViewById<
  TData = Awaited<ReturnType<typeof deleteCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Custom View By Id
 */

export function useDeleteCustomViewById<
  TData = Awaited<ReturnType<typeof deleteCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: DeleteCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCustomViewByIdQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Custom View By Id
 */
export const prefetchDeleteCustomViewByIdQuery = async <
  TData = Awaited<ReturnType<typeof deleteCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: DeleteCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCustomViewByIdQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Custom View By Id
 */
export const invalidateDeleteCustomViewById = async (
  queryClient: QueryClient,
  { viewId }: DeleteCustomViewByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteCustomViewByIdQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Get Custom View By Id
 */
export const getCustomViewById = (
  { viewId }: GetCustomViewByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewResponse>(
    { url: `/v2/data-harmonization/custom-views/${viewId}`, method: 'GET', signal },
    options,
  );
};

export const getGetCustomViewByIdQueryKey = ({ viewId }: GetCustomViewByIdPathParameters) => {
  return ['v2', 'data-harmonization', 'custom-views', viewId] as const;
};

export const getGetCustomViewByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCustomViewByIdQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomViewById>>> = ({ signal }) =>
    getCustomViewById({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCustomViewById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCustomViewByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getCustomViewById>>>;
export type GetCustomViewByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCustomViewById<
  TData = Awaited<ReturnType<typeof getCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewById<
  TData = Awaited<ReturnType<typeof getCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewById<
  TData = Awaited<ReturnType<typeof getCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Custom View By Id
 */

export function useGetCustomViewById<
  TData = Awaited<ReturnType<typeof getCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCustomViewByIdQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Custom View By Id
 */
export const prefetchGetCustomViewByIdQuery = async <
  TData = Awaited<ReturnType<typeof getCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetCustomViewByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCustomViewByIdQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Custom View By Id
 */
export const invalidateGetCustomViewById = async (
  queryClient: QueryClient,
  { viewId }: GetCustomViewByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCustomViewByIdQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Update Custom View By Id
 */
export const updateCustomViewById = (
  { viewId }: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewResponse>(
    {
      url: `/v2/data-harmonization/custom-views/${viewId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateCustomViewByIdQueryKey = (
  { viewId }: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest?: BodyType<TLCustomViewUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'custom-views', viewId, tLCustomViewUpdateRequest] as const;
};

export const getUpdateCustomViewByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof updateCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateCustomViewByIdQueryKey({ viewId }, tLCustomViewUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateCustomViewById>>> = ({ signal }) =>
    updateCustomViewById({ viewId }, tLCustomViewUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateCustomViewById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateCustomViewByIdQueryResult = NonNullable<Awaited<ReturnType<typeof updateCustomViewById>>>;
export type UpdateCustomViewByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateCustomViewById<
  TData = Awaited<ReturnType<typeof updateCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCustomViewById>>,
          TError,
          Awaited<ReturnType<typeof updateCustomViewById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCustomViewById<
  TData = Awaited<ReturnType<typeof updateCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCustomViewById>>,
          TError,
          Awaited<ReturnType<typeof updateCustomViewById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCustomViewById<
  TData = Awaited<ReturnType<typeof updateCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Custom View By Id
 */

export function useUpdateCustomViewById<
  TData = Awaited<ReturnType<typeof updateCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateCustomViewByIdQueryOptions({ viewId }, tLCustomViewUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Custom View By Id
 */
export const prefetchUpdateCustomViewByIdQuery = async <
  TData = Awaited<ReturnType<typeof updateCustomViewById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCustomViewById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateCustomViewByIdQueryOptions({ viewId }, tLCustomViewUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Custom View By Id
 */
export const invalidateUpdateCustomViewById = async (
  queryClient: QueryClient,
  { viewId }: UpdateCustomViewByIdPathParameters,
  tLCustomViewUpdateRequest: BodyType<TLCustomViewUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateCustomViewByIdQueryKey({ viewId }, tLCustomViewUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Replace Custom View
 */
export const replaceCustomView = (
  { viewId }: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewResponse>(
    {
      url: `/v2/data-harmonization/custom-views/${viewId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewReplaceRequest,
      signal,
    },
    options,
  );
};

export const getReplaceCustomViewQueryKey = (
  { viewId }: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest?: BodyType<TLCustomViewReplaceRequest>,
) => {
  return ['PUT', 'v2', 'data-harmonization', 'custom-views', viewId, tLCustomViewReplaceRequest] as const;
};

export const getReplaceCustomViewQueryOptions = <
  TData = Awaited<ReturnType<typeof replaceCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof replaceCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getReplaceCustomViewQueryKey({ viewId }, tLCustomViewReplaceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof replaceCustomView>>> = ({ signal }) =>
    replaceCustomView({ viewId }, tLCustomViewReplaceRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof replaceCustomView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ReplaceCustomViewQueryResult = NonNullable<Awaited<ReturnType<typeof replaceCustomView>>>;
export type ReplaceCustomViewQueryError = ErrorType<TLHTTPValidationError>;

export function useReplaceCustomView<
  TData = Awaited<ReturnType<typeof replaceCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof replaceCustomView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof replaceCustomView>>,
          TError,
          Awaited<ReturnType<typeof replaceCustomView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReplaceCustomView<
  TData = Awaited<ReturnType<typeof replaceCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof replaceCustomView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof replaceCustomView>>,
          TError,
          Awaited<ReturnType<typeof replaceCustomView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReplaceCustomView<
  TData = Awaited<ReturnType<typeof replaceCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof replaceCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Replace Custom View
 */

export function useReplaceCustomView<
  TData = Awaited<ReturnType<typeof replaceCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof replaceCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getReplaceCustomViewQueryOptions({ viewId }, tLCustomViewReplaceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Replace Custom View
 */
export const prefetchReplaceCustomViewQuery = async <
  TData = Awaited<ReturnType<typeof replaceCustomView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof replaceCustomView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getReplaceCustomViewQueryOptions({ viewId }, tLCustomViewReplaceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Replace Custom View
 */
export const invalidateReplaceCustomView = async (
  queryClient: QueryClient,
  { viewId }: ReplaceCustomViewPathParameters,
  tLCustomViewReplaceRequest: BodyType<TLCustomViewReplaceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getReplaceCustomViewQueryKey({ viewId }, tLCustomViewReplaceRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Backfill Custom View Executions
 */
export const backfillCustomViewExecutions = (
  { viewId }: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewBackfillResponse>(
    {
      url: `/v2/data-harmonization/custom-views/${viewId}/backfill`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewBackfillRequest,
      signal,
    },
    options,
  );
};

export const getBackfillCustomViewExecutionsQueryKey = (
  { viewId }: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest?: BodyType<TLCustomViewBackfillRequest>,
) => {
  return ['POST', 'v2', 'data-harmonization', 'custom-views', viewId, 'backfill', tLCustomViewBackfillRequest] as const;
};

export const getBackfillCustomViewExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backfillCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getBackfillCustomViewExecutionsQueryKey({ viewId }, tLCustomViewBackfillRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof backfillCustomViewExecutions>>> = ({ signal }) =>
    backfillCustomViewExecutions({ viewId }, tLCustomViewBackfillRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BackfillCustomViewExecutionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof backfillCustomViewExecutions>>
>;
export type BackfillCustomViewExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useBackfillCustomViewExecutions<
  TData = Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof backfillCustomViewExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
          TError,
          Awaited<ReturnType<typeof backfillCustomViewExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBackfillCustomViewExecutions<
  TData = Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backfillCustomViewExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
          TError,
          Awaited<ReturnType<typeof backfillCustomViewExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBackfillCustomViewExecutions<
  TData = Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backfillCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Backfill Custom View Executions
 */

export function useBackfillCustomViewExecutions<
  TData = Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backfillCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBackfillCustomViewExecutionsQueryOptions({ viewId }, tLCustomViewBackfillRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Backfill Custom View Executions
 */
export const prefetchBackfillCustomViewExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof backfillCustomViewExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backfillCustomViewExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBackfillCustomViewExecutionsQueryOptions({ viewId }, tLCustomViewBackfillRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Backfill Custom View Executions
 */
export const invalidateBackfillCustomViewExecutions = async (
  queryClient: QueryClient,
  { viewId }: BackfillCustomViewExecutionsPathParameters,
  tLCustomViewBackfillRequest: BodyType<TLCustomViewBackfillRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBackfillCustomViewExecutionsQueryKey({ viewId }, tLCustomViewBackfillRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get All Custom View Executions By View
 */
export const getAllCustomViewExecutionsByView = (
  { viewId }: GetAllCustomViewExecutionsByViewPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewExecutionsResponse>(
    { url: `/v2/data-harmonization/custom-views/${viewId}/executions`, method: 'GET', signal },
    options,
  );
};

export const getGetAllCustomViewExecutionsByViewQueryKey = ({
  viewId,
}: GetAllCustomViewExecutionsByViewPathParameters) => {
  return ['v2', 'data-harmonization', 'custom-views', viewId, 'executions'] as const;
};

export const getGetAllCustomViewExecutionsByViewQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetAllCustomViewExecutionsByViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllCustomViewExecutionsByViewQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>> = ({ signal }) =>
    getAllCustomViewExecutionsByView({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllCustomViewExecutionsByViewQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>
>;
export type GetAllCustomViewExecutionsByViewQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllCustomViewExecutionsByView<
  TData = Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllCustomViewExecutionsByViewPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
          TError,
          Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCustomViewExecutionsByView<
  TData = Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllCustomViewExecutionsByViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
          TError,
          Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCustomViewExecutionsByView<
  TData = Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllCustomViewExecutionsByViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Custom View Executions By View
 */

export function useGetAllCustomViewExecutionsByView<
  TData = Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetAllCustomViewExecutionsByViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllCustomViewExecutionsByViewQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Custom View Executions By View
 */
export const prefetchGetAllCustomViewExecutionsByViewQuery = async <
  TData = Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetAllCustomViewExecutionsByViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCustomViewExecutionsByView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllCustomViewExecutionsByViewQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Custom View Executions By View
 */
export const invalidateGetAllCustomViewExecutionsByView = async (
  queryClient: QueryClient,
  { viewId }: GetAllCustomViewExecutionsByViewPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllCustomViewExecutionsByViewQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Create Custom View Group
 */
export const createCustomViewGroup = (
  { viewId }: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataHarmonizationV2GroupResponse>(
    {
      url: `/v2/data-harmonization/custom-views/${viewId}/groups`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCustomViewGroupCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateCustomViewGroupQueryKey = (
  { viewId }: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest?: BodyType<TLCustomViewGroupCreateRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'custom-views',
    viewId,
    'groups',
    tLCustomViewGroupCreateRequest,
  ] as const;
};

export const getCreateCustomViewGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof createCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateCustomViewGroupQueryKey({ viewId }, tLCustomViewGroupCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCustomViewGroup>>> = ({ signal }) =>
    createCustomViewGroup({ viewId }, tLCustomViewGroupCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createCustomViewGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateCustomViewGroupQueryResult = NonNullable<Awaited<ReturnType<typeof createCustomViewGroup>>>;
export type CreateCustomViewGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCustomViewGroup<
  TData = Awaited<ReturnType<typeof createCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCustomViewGroup>>,
          TError,
          Awaited<ReturnType<typeof createCustomViewGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCustomViewGroup<
  TData = Awaited<ReturnType<typeof createCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCustomViewGroup>>,
          TError,
          Awaited<ReturnType<typeof createCustomViewGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCustomViewGroup<
  TData = Awaited<ReturnType<typeof createCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Custom View Group
 */

export function useCreateCustomViewGroup<
  TData = Awaited<ReturnType<typeof createCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCustomViewGroupQueryOptions({ viewId }, tLCustomViewGroupCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Custom View Group
 */
export const prefetchCreateCustomViewGroupQuery = async <
  TData = Awaited<ReturnType<typeof createCustomViewGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCustomViewGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCustomViewGroupQueryOptions({ viewId }, tLCustomViewGroupCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Custom View Group
 */
export const invalidateCreateCustomViewGroup = async (
  queryClient: QueryClient,
  { viewId }: CreateCustomViewGroupPathParameters,
  tLCustomViewGroupCreateRequest: BodyType<TLCustomViewGroupCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateCustomViewGroupQueryKey({ viewId }, tLCustomViewGroupCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Custom View Links By Id
 */
export const getCustomViewLinksById = (
  { viewId }: GetCustomViewLinksByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewLinksResponse>(
    { url: `/v2/data-harmonization/custom-views/${viewId}/links`, method: 'GET', signal },
    options,
  );
};

export const getGetCustomViewLinksByIdQueryKey = ({ viewId }: GetCustomViewLinksByIdPathParameters) => {
  return ['v2', 'data-harmonization', 'custom-views', viewId, 'links'] as const;
};

export const getGetCustomViewLinksByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getCustomViewLinksById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetCustomViewLinksByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewLinksById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCustomViewLinksByIdQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomViewLinksById>>> = ({ signal }) =>
    getCustomViewLinksById({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCustomViewLinksById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCustomViewLinksByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getCustomViewLinksById>>>;
export type GetCustomViewLinksByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCustomViewLinksById<
  TData = Awaited<ReturnType<typeof getCustomViewLinksById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewLinksByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewLinksById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewLinksById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewLinksById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewLinksById<
  TData = Awaited<ReturnType<typeof getCustomViewLinksById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewLinksByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewLinksById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCustomViewLinksById>>,
          TError,
          Awaited<ReturnType<typeof getCustomViewLinksById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCustomViewLinksById<
  TData = Awaited<ReturnType<typeof getCustomViewLinksById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetCustomViewLinksByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewLinksById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Custom View Links By Id
 */

export function useGetCustomViewLinksById<
  TData = Awaited<ReturnType<typeof getCustomViewLinksById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetCustomViewLinksByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewLinksById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCustomViewLinksByIdQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Custom View Links By Id
 */
export const prefetchGetCustomViewLinksByIdQuery = async <
  TData = Awaited<ReturnType<typeof getCustomViewLinksById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetCustomViewLinksByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCustomViewLinksById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCustomViewLinksByIdQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Custom View Links By Id
 */
export const invalidateGetCustomViewLinksById = async (
  queryClient: QueryClient,
  { viewId }: GetCustomViewLinksByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCustomViewLinksByIdQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * Temp endpoint: trigger a view against the latest completed batch for its dashboard.
 * @summary Trigger Custom View Execution
 */
export const triggerCustomViewExecution = (
  { viewId }: TriggerCustomViewExecutionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCustomViewExecutionResponse>(
    { url: `/v2/data-harmonization/custom-views/${viewId}/trigger`, method: 'POST', signal },
    options,
  );
};

export const getTriggerCustomViewExecutionQueryKey = ({ viewId }: TriggerCustomViewExecutionPathParameters) => {
  return ['POST', 'v2', 'data-harmonization', 'custom-views', viewId, 'trigger'] as const;
};

export const getTriggerCustomViewExecutionQueryOptions = <
  TData = Awaited<ReturnType<typeof triggerCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: TriggerCustomViewExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getTriggerCustomViewExecutionQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof triggerCustomViewExecution>>> = ({ signal }) =>
    triggerCustomViewExecution({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof triggerCustomViewExecution>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type TriggerCustomViewExecutionQueryResult = NonNullable<Awaited<ReturnType<typeof triggerCustomViewExecution>>>;
export type TriggerCustomViewExecutionQueryError = ErrorType<TLHTTPValidationError>;

export function useTriggerCustomViewExecution<
  TData = Awaited<ReturnType<typeof triggerCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TriggerCustomViewExecutionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerCustomViewExecution>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof triggerCustomViewExecution>>,
          TError,
          Awaited<ReturnType<typeof triggerCustomViewExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTriggerCustomViewExecution<
  TData = Awaited<ReturnType<typeof triggerCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TriggerCustomViewExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerCustomViewExecution>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof triggerCustomViewExecution>>,
          TError,
          Awaited<ReturnType<typeof triggerCustomViewExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTriggerCustomViewExecution<
  TData = Awaited<ReturnType<typeof triggerCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TriggerCustomViewExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Trigger Custom View Execution
 */

export function useTriggerCustomViewExecution<
  TData = Awaited<ReturnType<typeof triggerCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: TriggerCustomViewExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getTriggerCustomViewExecutionQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Trigger Custom View Execution
 */
export const prefetchTriggerCustomViewExecutionQuery = async <
  TData = Awaited<ReturnType<typeof triggerCustomViewExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: TriggerCustomViewExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerCustomViewExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getTriggerCustomViewExecutionQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Trigger Custom View Execution
 */
export const invalidateTriggerCustomViewExecution = async (
  queryClient: QueryClient,
  { viewId }: TriggerCustomViewExecutionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getTriggerCustomViewExecutionQueryKey({ viewId }) }, options);

  return queryClient;
};
